import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeOff, UserPlus } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { RegisterData } from '../types'
import LoadingSpinner from '../components/LoadingSpinner'
import PolicyAgreement from '../components/PolicyAgreement'
import ErrorModal from '../components/ErrorModal'

export default function RegisterPage() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [showPolicyAgreement, setShowPolicyAgreement] = useState(false)
  const [pendingRegisterData, setPendingRegisterData] = useState<RegisterData | null>(null)
  const { register: registerUser } = useAuth()
  const navigate = useNavigate()
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm<RegisterData>()
  const password = watch('password')

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const onSubmit = async (data: RegisterData) => {
    if (loading) return

    if (!validateEmail(data.email)) {
      setError('请输入有效的邮箱地址')
      setShowErrorModal(true)
      return
    }

    if (data.password.length < 6) {
      setError('密码必须至少6个字符')
      setShowErrorModal(true)
      return
    }

    if (data.password !== data.confirmPassword) {
      setError('密码不匹配')
      setShowErrorModal(true)
      return
    }

    // Show policy agreement before registration
    setPendingRegisterData(data)
    setShowPolicyAgreement(true)
  }

  const performRegister = async () => {
    if (!pendingRegisterData) return

    setLoading(true)
    setError('')

    try {
      const result = await registerUser(pendingRegisterData)
      if (result.success) {
        navigate('/verify', { 
          state: { email: pendingRegisterData.email }
        })
      } else {
        setError(result.error || '注册失败，请重试')
        setShowErrorModal(true)
      }
    } catch (err) {
      setError('注册失败，请重试')
      setShowErrorModal(true)
    } finally {
      setLoading(false)
      setShowPolicyAgreement(false)
      setPendingRegisterData(null)
    }
  }

  const handlePolicyDecline = () => {
    setShowPolicyAgreement(false)
    setPendingRegisterData(null)
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <Link to="/" className="text-2xl font-bold text-gray-900">
              Sku Studio
            </Link>
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            创建您的账户
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            已有账户？{' '}
            <Link to="/login" className="text-blue-600 hover:text-blue-500">
              立即登录
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  邮箱地址
                </label>
                <div className="mt-1">
                  <input
                    {...register('email', {
                      required: '请输入邮箱地址',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: '请输入有效的邮箱地址'
                      }
                    })}
                    type="email"
                    autoComplete="email"
                    className="input"
                    placeholder="your@email.com"
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  密码
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('password', {
                      required: '请输入密码',
                      minLength: {
                        value: 6,
                        message: '密码至少需要6位字符'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input pr-10"
                    placeholder="输入密码（至少6位）"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  确认密码
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('confirmPassword', {
                      required: '请确认密码',
                      validate: value => value === password || '两次输入的密码不一致'
                    })}
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className="input pr-10"
                    placeholder="再次输入密码"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center items-center gap-2 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4" />
                      创建账户
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {showPolicyAgreement && (
        <PolicyAgreement
          title="服务条款和隐私政策"
          subtitle="继续注册前请阅读并同意"
          onAgree={performRegister}
          onDecline={handlePolicyDecline}
        />
      )}

      {/* 错误弹窗 */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="注册失败"
        message={error}
      />
    </>
  )
}